import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { LemonadeOnboarding } from '@/components/auth/LemonadeOnboarding';
import { LemonadeLogin } from '@/components/auth/LemonadeLogin';
import { ForgotPassword } from '@/components/auth/ForgotPassword';
import { DemoBypassBanner } from '@/components/auth/DemoBypassBanner';
import { useAuth } from '@/hooks/useAuth';
import { SEOHead } from '@/components/SEOHead';
import reklaimWordmark from '@/assets/reklaim-wordmark.png';
import protectWordmark from '@/assets/protect-wordmark.svg';
import { RotateCcw, HelpCircle, Menu } from 'lucide-react';

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, onboardingStatus, isLoading } = useAuth();
  const modeFromUrl = searchParams.get('mode');
  const demoMode = searchParams.get('demo') === '1';
  const [mode, setMode] = useState<'onboarding' | 'login' | 'forgot-password'>(
    modeFromUrl === 'login' ? 'login' : 'onboarding'
  );
  
  // Track if user is in the middle of signup flow - prevents auto-redirect
  const [isCompletingSignup, setIsCompletingSignup] = useState(false);

  // Capture URL parameters
  const refCode = searchParams.get('ref');
  const utmSource = searchParams.get('utm_source');
  const utmMedium = searchParams.get('utm_medium');
  const utmCampaign = searchParams.get('utm_campaign');
  
  // Get email from URL or localStorage
  const emailFromUrl = searchParams.get('email');
  const initialEmail = emailFromUrl || localStorage.getItem('reklaim_email') || undefined;

  // Store attribution data in localStorage for persistence
  useEffect(() => {
    const existingAttribution = localStorage.getItem('reklaim_attribution');
    const attribution = existingAttribution ? JSON.parse(existingAttribution) : {};
    
    if (refCode) attribution.ref = refCode;
    if (utmSource) attribution.source = utmSource;
    if (utmMedium) attribution.medium = utmMedium;
    if (utmCampaign) attribution.campaign = utmCampaign;
    
    if (refCode || utmSource || utmMedium || utmCampaign) {
      attribution.landedAt = attribution.landedAt || new Date().toISOString();
      localStorage.setItem('reklaim_attribution', JSON.stringify(attribution));
    }
  }, [refCode, utmSource, utmMedium, utmCampaign]);

  // Callback for when signup flow starts password step
  const handleSignupStarted = useCallback(() => {
    setIsCompletingSignup(true);
  }, []);

  const sourceParam = searchParams.get('source');
  const rawRedirect = searchParams.get('redirect');
  const safeRedirect = rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')
    ? rawRedirect
    : null;
  const postAuthRedirect = safeRedirect
    ?? (sourceParam === 'protect' ? '/dashboard/protect?source=protect' : '/dashboard');

  // Callback for when signup completes (user finishes or skips profile wizard)
  const handleSignupComplete = useCallback(() => {
    setIsCompletingSignup(false);
    navigate(postAuthRedirect);
  }, [navigate, postAuthRedirect]);

  // Redirect authenticated users to dashboard - BUT NOT during signup flow
  useEffect(() => {
    if (isAuthenticated && !isLoading && !isCompletingSignup) {
      navigate(postAuthRedirect);
    }
  }, [isAuthenticated, isLoading, isCompletingSignup, navigate, postAuthRedirect]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse">
          <img 
            src={reklaimWordmark} 
            alt="Reklaim" 
            className="h-8 opacity-50"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEOHead
        title="Get Started | Reklaim"
        description="Create your free Reklaim account and start earning rewards from your data."
        canonical="https://reklaimyou.com/auth"
      />

      {demoMode && <DemoBypassBanner onSuccess={() => navigate(postAuthRedirect)} />}

      {/* Lemonade-style Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-4">
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden">
            <Menu className="h-5 w-5 text-gray-600" />
          </button>
          <Link to={sourceParam === 'protect' ? '/protect' : '/'}>
            {sourceParam === 'protect' ? (
              <div className="flex items-center gap-1.5">
                <img src={protectWordmark} alt="Protect" className="h-5" loading="eager" />
                <span className="text-[10px] text-gray-400">by</span>
                <img src={reklaimWordmark} alt="Reklaim" className="h-3 opacity-60" loading="eager" />
              </div>
            ) : (
              <img src={reklaimWordmark} alt="Reklaim" className="h-6" />
            )}
          </Link>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setMode('onboarding')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Start over"
          >
            <RotateCcw className="h-5 w-5 text-gray-400" />
          </button>
          <button 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Help"
          >
            <HelpCircle className="h-5 w-5 text-gray-400" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        {mode === 'onboarding' ? (
          <LemonadeOnboarding 
            referralCode={refCode} 
            initialEmail={initialEmail}
            source={sourceParam}
            onSwitchToLogin={() => setMode('login')}
            onSignupStarted={handleSignupStarted}
            onSignupComplete={handleSignupComplete}
            onForgotPassword={() => setMode('forgot-password')}
          />
        ) : mode === 'login' ? (
          <LemonadeLogin 
            onSwitchToSignup={() => setMode('onboarding')} 
            onForgotPassword={() => setMode('forgot-password')}
          />
        ) : (
          <ForgotPassword onBack={() => setMode('login')} />
        )}
      </main>
    </div>
  );
};

export default Auth;
